import { Add, Close, Save } from '@mui/icons-material';
import {
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Box,
  ButtonGroup,
  Button,
  Divider
} from '@mui/material';
import { useQuery } from 'react-query';
import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useHistory, useParams, Prompt } from 'react-router-dom';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import React, { FC, useState } from 'react';
import {
  getHoaPaymentFrequencyOptions,
  getHoaStreetOwnerOptions,
  putNeighborhoodHoaInfo
} from '../../../../fetch';
import { formatMoney, urlRegex } from '../../../../helpers';
import {
  INeighborhoodDetailHoaInfo,
  INeighborhoodDetailHoaInfoPut,
  IDropdownResponse
} from '../../../../models';
import { Loader, ImageModal } from '../../../../components';
import { HoaImages } from './hoa-images';

const HOASchema = Yup.object().shape({
  neighborhoodId: Yup.string(),
  homeownersAssociationName: Yup.string().when(['neighborhoodId', 'selectedPage'], {
    is: neighborhoodId => {
      return neighborhoodId === 'add';
    },
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  homeownersAssociationWebsite: Yup.string().matches(urlRegex, 'URL is not valid').nullable(),
  hoaManagedByThirdParty: Yup.boolean(),
  hoaThirdPartyManagementCompany: Yup.string()
    .nullable()
    .max(250, 'Max 250 characters')
    .when('hoaManagedByThirdParty', {
      is: true,
      then: Yup.string().max(250, 'Max 250 characters').required('Required')
    }),
  hoaThirdPartyManagementWebsite: Yup.string().matches(urlRegex, 'URL is not valid').nullable(),
  hoaAnnualDues: Yup.string().when(['neighborhoodId'], {
    is: neighborhoodId => {
      return neighborhoodId === 'add';
    },
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  hoaPaymentFrequency: Yup.string().when(['neighborhoodId'], {
    is: neighborhoodId => {
      return neighborhoodId === 'add';
    },
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  streetOwner: Yup.string().when(['neighborhoodId'], {
    is: neighborhoodId => {
      return neighborhoodId === 'add';
    },
    then: Yup.string().required('Required'),
    otherwise: Yup.string().notRequired()
  }),
  covenantsAndRestrictions: Yup.array().nullable(),
  covenantsDb: Yup.string().nullable(),
  covenantsPg: Yup.string().nullable(),
  amendmentsToDeclarations: Yup.array().nullable(),
  architecturalGuidelines: Yup.array().nullable()
});

interface IHOAInformationProps {
  hoaInfo: INeighborhoodDetailHoaInfo;
  isLoadingNeighborhoodHoaInfo: boolean;
  refetchHoaInfo: () => void;
}

export const HOAInformationTab: FC<IHOAInformationProps> = ({
  hoaInfo,
  isLoadingNeighborhoodHoaInfo,
  refetchHoaInfo
}) => {
  const [selectedImage, setSelectedImage] = useState<string>('');
  const { enqueueSnackbar } = useSnackbar();
  const { neighborhoodId }: { neighborhoodId: string } = useParams();
  const history = useHistory();

  const { data: hoaPaymentFrequency } = useQuery<IDropdownResponse[], Error>(
    ['getHoaPaymentFrequencyOptions'],
    () => getHoaPaymentFrequencyOptions()
  );

  const { data: hoaStreetOwners } = useQuery<IDropdownResponse[], Error>(
    ['getHoaStreetOwnerOptions'],
    () => getHoaStreetOwnerOptions()
  );

  return (
    <>
      {isLoadingNeighborhoodHoaInfo && (
        <Box mt={1} minHeight="30rem" display="flex" flexDirection="column" justifyContent="center">
          <Loader type="inline" position="centered">
            Loading...
          </Loader>
        </Box>
      )}
      {!isLoadingNeighborhoodHoaInfo && (
        <Formik
          enableReinitialize={true}
          initialValues={{
            homeownersAssociationName: hoaInfo?.homeownersAssociationName ?? '',
            homeownersAssociationWebsite: hoaInfo?.homeownersAssociationWebsite ?? '',
            hoaManagedByThirdParty: hoaInfo?.hoaManagedByThirdParty ?? false,
            hoaThirdPartyManagementCompany: hoaInfo?.hoaThirdPartyManagementCompany ?? '',
            hoaThirdPartyManagementWebsite: hoaInfo?.hoaThirdPartyManagementWebsite ?? '',
            hoaAnnualDues: hoaInfo?.hoaAnnualDues ? formatMoney(hoaInfo?.hoaAnnualDues, 2) : '',
            hoaPaymentFrequency: hoaInfo?.hoaPaymentFrequency ?? 'None',
            streetOwner: hoaInfo?.streetOwner ?? 'LocalMunicipality',
            covenantsAndRestrictions: hoaInfo?.covenantsAndRestrictions ?? null,
            covenantsDb: hoaInfo?.covenantsDb ?? '',
            covenantsPg: hoaInfo?.covenantsPg ?? '',
            amendmentsToDeclarations: hoaInfo?.amendmentsToDeclarations ?? null,
            architecturalGuidelines: hoaInfo?.architecturalGuidelines ?? null
          }}
          validationSchema={HOASchema}
          onSubmit={async (values, actions) => {
            if (neighborhoodId !== 'add') {
              try {
                const data: INeighborhoodDetailHoaInfoPut = {
                  ...values,
                  hoaAnnualDues: Number(values.hoaAnnualDues.replace('$', '').replaceAll(',', ''))
                };
                await putNeighborhoodHoaInfo(neighborhoodId, data);
                await refetchHoaInfo();
                enqueueSnackbar('Neighborhood has been updated successfully!', {
                  variant: 'success'
                });
              } catch (error) {
                enqueueSnackbar(
                  error?.Detail ?? 'Something went wrong. Neighborhood was not updated.',
                  {
                    variant: 'error'
                  }
                );
              }
            }
          }}
        >
          {({
            isSubmitting,
            values,
            initialValues,
            setFieldValue,
            errors,
            touched,
            handleSubmit,
            dirty,
            isValid,
            handleBlur
          }) => {
            return (
              <Form onSubmit={handleSubmit}>
                {isSubmitting && (
                  <Loader type="fullscreen" position="centered">
                    Saving...
                  </Loader>
                )}
                <Prompt
                  when={!deepEqual(initialValues, values)}
                  message="You have unsaved changes, are you sure you want to exit?"
                />
                <Box mt={2}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Homeowners Association Name"
                        name="homeownersAssociationName"
                        size="small"
                        required
                        value={values.homeownersAssociationName}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('homeownersAssociationName', e.target.value)}
                        error={Boolean(
                          touched.homeownersAssociationName && errors.homeownersAssociationName
                        )}
                        helperText={
                          touched.homeownersAssociationName && errors.homeownersAssociationName
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Homeowners Association Website"
                        name="homeownersAssociationWebsite"
                        size="small"
                        value={values.homeownersAssociationWebsite}
                        onBlur={handleBlur}
                        onChange={e =>
                          setFieldValue('homeownersAssociationWebsite', e.target.value)
                        }
                        error={Boolean(
                          touched.homeownersAssociationWebsite &&
                            errors.homeownersAssociationWebsite
                        )}
                        helperText={
                          touched.homeownersAssociationWebsite &&
                          errors.homeownersAssociationWebsite
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={values.hoaManagedByThirdParty}
                            id="hoaManagedByThirdParty"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              setFieldValue(
                                'hoaManagedByThirdParty',
                                !values.hoaManagedByThirdParty
                              );
                              setFieldValue('hoaThirdPartyManagementCompany', ''); // Reset value to default
                              setFieldValue('hoaThirdPartyManagementWebsite', ''); // Reset value to default
                            }}
                          />
                        }
                        label="Homeowners Association Managed By 3rd Party"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Annual HOA Dues"
                        name="hoaAnnualDues"
                        size="small"
                        required
                        value={values.hoaAnnualDues}
                        onBlur={e => {
                          handleBlur(e);
                          setFieldValue('hoaAnnualDues', formatMoney(e.target.value, 2));
                        }}
                        onChange={e => setFieldValue('hoaAnnualDues', e.target.value)}
                        error={Boolean(touched.hoaAnnualDues && errors.hoaAnnualDues)}
                        helperText={touched.hoaAnnualDues && errors.hoaAnnualDues}
                      />
                    </Grid>
                    {values.hoaManagedByThirdParty && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="3rd Party Management Company Name"
                            name="hoaThirdPartyManagementCompany"
                            size="small"
                            value={values.hoaThirdPartyManagementCompany}
                            onBlur={handleBlur}
                            onChange={e =>
                              setFieldValue('hoaThirdPartyManagementCompany', e.target.value)
                            }
                            error={Boolean(
                              touched.hoaThirdPartyManagementCompany &&
                                errors.hoaThirdPartyManagementCompany
                            )}
                            helperText={
                              touched.hoaThirdPartyManagementCompany &&
                              errors.hoaThirdPartyManagementCompany
                            }
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            autoComplete="nope"
                            label="3rd Party Management Company Website"
                            name="hoaThirdPartyManagementWebsite"
                            size="small"
                            value={values.hoaThirdPartyManagementWebsite}
                            onBlur={handleBlur}
                            onChange={e =>
                              setFieldValue('hoaThirdPartyManagementWebsite', e.target.value)
                            }
                            error={Boolean(
                              touched.hoaThirdPartyManagementWebsite &&
                                errors.hoaThirdPartyManagementWebsite
                            )}
                            helperText={
                              touched.hoaThirdPartyManagementWebsite &&
                              errors.hoaThirdPartyManagementWebsite
                            }
                          />
                        </Grid>
                      </>
                    )}
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" required>
                        <FormLabel>HOA Payment Frequency</FormLabel>
                        <RadioGroup
                          aria-label="hoaPaymentFrequency"
                          name="hoaPaymentFrequency"
                          value={values.hoaPaymentFrequency}
                          onChange={e => setFieldValue('hoaPaymentFrequency', e.target.value)}
                        >
                          {hoaPaymentFrequency?.length > 0 &&
                            hoaPaymentFrequency.map(option => {
                              return (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={<Radio />}
                                  label={option.description}
                                />
                              );
                            })}
                        </RadioGroup>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl component="fieldset" required>
                        <FormLabel>Street Owner</FormLabel>
                        <RadioGroup
                          aria-label="streetOwner"
                          name="streetOwner"
                          value={values.streetOwner}
                          onChange={e => setFieldValue('streetOwner', e.target.value)}
                        >
                          {hoaStreetOwners?.length > 0 &&
                            hoaStreetOwners.map(option => {
                              return (
                                <FormControlLabel
                                  key={option.value}
                                  value={option.value}
                                  control={<Radio />}
                                  label={option.description}
                                />
                              );
                            })}
                        </RadioGroup>
                      </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                      <HoaImages
                        neighborhoodId={`${neighborhoodId}`}
                        photos={values.covenantsAndRestrictions}
                        documentOwner="covenantsAndRestrictions"
                        label="Covenants and Restrictions"
                        setSelectedImage={setSelectedImage}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Covenants DB"
                        name="covenantsDb"
                        size="small"
                        value={values.covenantsDb}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('covenantsDb', e.target.value)}
                        error={Boolean(touched.covenantsDb && errors.covenantsDb)}
                        helperText={touched.covenantsDb && errors.covenantsDb}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Covenants PG"
                        name="covenantsPg"
                        size="small"
                        value={values.covenantsPg}
                        onBlur={handleBlur}
                        onChange={e => setFieldValue('covenantsPg', e.target.value)}
                        error={Boolean(touched.covenantsPg && errors.covenantsPg)}
                        helperText={touched.covenantsPg && errors.covenantsPg}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <HoaImages
                        neighborhoodId={`${neighborhoodId}`}
                        photos={values.amendmentsToDeclarations}
                        documentOwner="amendmentsToDeclarations"
                        label="Amendments to Declarations"
                        setSelectedImage={setSelectedImage}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <HoaImages
                        neighborhoodId={`${neighborhoodId}`}
                        photos={values.architecturalGuidelines}
                        documentOwner="architecturalGuidelines"
                        label="Architectural Guidelines"
                        setSelectedImage={setSelectedImage}
                      />
                    </Grid>
                  </Grid>
                  <ImageModal
                    isOpen={!!selectedImage}
                    onClose={() => setSelectedImage('')}
                    source={selectedImage}
                  />
                </Box>
                <Box mt={1} mb={0.5}>
                  <Divider />
                </Box>
                <ButtonGroup>
                  <Button
                    disabled={!dirty || isSubmitting || !isValid}
                    type="submit"
                    startIcon={neighborhoodId ? <Save /> : <Add />}
                    variant="contained"
                    color="primary"
                  >
                    Save
                  </Button>
                  <Button
                    type="button"
                    variant="contained"
                    color="inherit"
                    startIcon={<Close />}
                    onClick={() => {
                      history.push('/development/neighborhoods');
                    }}
                  >
                    Cancel
                  </Button>
                </ButtonGroup>
              </Form>
            );
          }}
        </Formik>
      )}
    </>
  );
};
